import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { DashboardLayout, Card, Grid, CardIcon, CardTitle, CardValue, CardDescription, ActionButton } from '../../components/DashboardLayout';
import glassTheme from '../../styles/glassTheme';
import { getAllRoles, getAllPermissions, getActionLogs } from '../../services/rbacApi';
import { dashboardApi, SuperAdminDashboardData } from '../../services/dashboardApi';

const StatCard = styled(Card)`
  background: linear-gradient(135deg, rgba(255, 0, 0, 0.1) 0%, rgba(255, 100, 0, 0.1) 100%);
  border: 1px solid rgba(255, 0, 0, 0.3);
`;

const QuickActionCard = styled(Card)`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;

  &:hover {
    background: rgba(255, 0, 0, 0.1);
  }
`;

const QuickActionIcon = styled.div`
  font-size: 3rem;
`;

const QuickActionTitle = styled.div`
  font-weight: 600;
  color: ${glassTheme.colors.text.primary};
`;

const QuickActionDesc = styled.div`
  font-size: 0.85rem;
  color: ${glassTheme.colors.text.muted};
`;

const TableContainer = styled(Card)`
  grid-column: 1 / -1;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid ${glassTheme.colors.border};
  color: ${glassTheme.colors.text.secondary};
  font-weight: 600;
`;

const TableRow = styled.tr`
  &:hover {
    background: rgba(0, 0, 0, 0.2);
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  border-bottom: 1px solid ${glassTheme.colors.border};
  color: ${glassTheme.colors.text.primary};
`;

const StatusBadge = styled.span<{ status: 'active' | 'inactive' | 'error' }>`
  padding: 0.25rem 0.75rem;
  border-radius: ${glassTheme.borderRadius.md};
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => {
    switch (props.status) {
      case 'active': return 'rgba(0, 255, 0, 0.2)';
      case 'inactive': return 'rgba(255, 255, 0, 0.2)';
      case 'error': return 'rgba(255, 0, 0, 0.2)';
      default: return 'rgba(128, 128, 128, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'active': return '#00FF00';
      case 'inactive': return '#FFFF00';
      case 'error': return '#FF0000';
      default: return '#888888';
    }
  }};
`;

const RecentLogsContainer = styled(Card)`
  grid-column: 1 / -1;
`;

const LogItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.md};
  margin-bottom: 0.5rem;
  gap: 1rem;
`;

const LogIcon = styled.div<{ result: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => {
    switch (props.result) {
      case 'success': return 'rgba(0, 255, 0, 0.2)';
      case 'failure': return 'rgba(255, 0, 0, 0.2)';
      case 'partial': return 'rgba(255, 255, 0, 0.2)';
      default: return 'rgba(128, 128, 128, 0.2)';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
`;

const LogContent = styled.div`
  flex: 1;
`;

const LogAction = styled.div`
  color: ${glassTheme.colors.text.primary};
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const LogDetails = styled.div`
  color: ${glassTheme.colors.text.muted};
  font-size: 0.85rem;
`;

const LogTime = styled.div`
  color: ${glassTheme.colors.text.muted};
  font-size: 0.8rem;
  white-space: nowrap;
`;

export const SuperAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalRoles: 0,
    totalPermissions: 0,
    totalAdmins: 0,
    activeUsers: 0
  });
  const [recentLogs, setRecentLogs] = useState<any[]>([]);
  const [data, setData] = useState<SuperAdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      // Real counts from the dashboard API, replacing the hardcoded
      // totalPermissions/totalAdmins/activeUsers placeholders.
      const [dash, logsData] = await Promise.all([
        dashboardApi.getSuperAdmin(),
        getActionLogs({ page: 1, limit: 5 }).catch(() => ({ logs: [] as any[] }))
      ]);

      setData(dash);
      setStats({
        totalRoles: dash.stats.roles,
        totalPermissions: dash.stats.permissions,
        totalAdmins: dash.stats.admins,
        activeUsers: dash.stats.activeToday
      });

      // Prefer the API's audit trail; fall back to the RBAC action-log endpoint.
      setRecentLogs(
        dash.recentActions.length
          ? dash.recentActions.map((a) => ({
            action: a.action,
            targetType: a.target,
            createdAt: a.timestamp,
            adminName: a.by
          }))
          : logsData.logs
      );
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      icon: '👥',
      title: '角色管理',
      description: '管理系统角色和权限',
      onClick: () => navigate('/admin/roles')
    },
    {
      icon: '🔐',
      title: '权限配置',
      description: '配置系统权限',
      onClick: () => navigate('/admin/permissions')
    },
    {
      icon: '👑',
      title: '管理员档案',
      description: '查看管理员信息',
      onClick: () => navigate('/admin/admin-profiles')
    },
    {
      icon: '📋',
      title: '操作日志',
      description: '查看系统操作记录',
      onClick: () => navigate('/admin/action-logs')
    },
    {
      icon: '⚙️',
      title: '系统设置',
      description: '管理系统配置',
      onClick: () => navigate('/admin/settings')
    },
    {
      icon: '🔧',
      title: '系统维护',
      description: '系统维护工具',
      onClick: () => navigate('/admin/maintenance')
    }
  ];

  // Measured, not asserted. Only values that are actually observable are shown;
  // the previous table claimed a response time and a backup age that were never
  // measured, which is worse than showing nothing in an operations console.
  const systemHealth = data
    ? [
      {
        name: '数据库连接',
        status: (data.health.database === 'connected' ? 'active' : 'error') as 'active' | 'error',
        value: data.health.database === 'connected' ? `已连接：${data.health.databaseName}` : '连接断开'
      },
      {
        name: 'API 服务',
        status: 'active' as const,
        value: `已运行 ${Math.floor(data.health.uptimeSeconds / 60)} 分钟`
      },
      {
        name: '今日活跃用户',
        status: 'active' as const,
        value: `${data.stats.activeToday} 人`
      },
      {
        name: '角色与权限',
        status: 'active' as const,
        value: `${data.stats.roles} 个角色 / ${data.stats.permissions} 项权限`
      }
    ]
    : [];

  return (
    <DashboardLayout
      title="超级管理员控制台"
      subtitle="完全的系统控制和管理权限"
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>加载中...</div>
      ) : (
        <>
          <Grid>
            <StatCard>
              <CardIcon>👑</CardIcon>
              <CardValue>{stats.totalRoles}</CardValue>
              <CardTitle>系统角色</CardTitle>
              <CardDescription>活跃的角色数量</CardDescription>
            </StatCard>

            <StatCard>
              <CardIcon>🔐</CardIcon>
              <CardValue>{stats.totalPermissions}</CardValue>
              <CardTitle>系统权限</CardTitle>
              <CardDescription>定义的权限数量</CardDescription>
            </StatCard>

            <StatCard>
              <CardIcon>👥</CardIcon>
              <CardValue>{stats.totalAdmins}</CardValue>
              <CardTitle>管理员总数</CardTitle>
              <CardDescription>系统管理员数量</CardDescription>
            </StatCard>

            <StatCard>
              <CardIcon>📊</CardIcon>
              <CardValue>{stats.activeUsers}</CardValue>
              <CardTitle>活跃用户</CardTitle>
              <CardDescription>今日活跃用户</CardDescription>
            </StatCard>

            {quickActions.map((action, index) => (
              <QuickActionCard key={index} onClick={action.onClick}>
                <QuickActionIcon>{action.icon}</QuickActionIcon>
                <QuickActionTitle>{action.title}</QuickActionTitle>
                <QuickActionDesc>{action.description}</QuickActionDesc>
              </QuickActionCard>
            ))}
          </Grid>

          <TableContainer>
            <CardTitle>系统健康状态</CardTitle>
            <Table>
              <thead>
                <tr>
                  <TableHeader>服务名称</TableHeader>
                  <TableHeader>状态</TableHeader>
                  <TableHeader>详细信息</TableHeader>
                </tr>
              </thead>
              <tbody>
                {systemHealth.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>
                      <StatusBadge status={item.status}>
                        {item.status === 'active' ? '正常' : '错误'}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>{item.value}</TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </TableContainer>

          <RecentLogsContainer>
            <CardTitle>最近系统操作</CardTitle>
            {recentLogs.length > 0 ? (
              recentLogs.map((log, index) => (
                <LogItem key={index}>
                  <LogIcon result={log.result}>
                    {log.result === 'success' ? '✓' : log.result === 'failure' ? '✗' : '⚠'}
                  </LogIcon>
                  <LogContent>
                    <LogAction>{log.action}</LogAction>
                    <LogDetails>{log.targetType || log.resource} - {log.adminName || log.admin?.name || '系统'}</LogDetails>
                  </LogContent>
                  <LogTime>{new Date(log.createdAt || log.timestamp).toLocaleString()}</LogTime>
                </LogItem>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '1rem', color: glassTheme.colors.text.muted }}>
                暂无操作记录
              </div>
            )}
          </RecentLogsContainer>
        </>
      )}
    </DashboardLayout>
  );
};
